/**
 * Stap 2: kies je gesprekspartners uit de tien personages van deze les.
 *
 * Aanklikken van een tegel opent de toelichting; kiezen gaat met de knop
 * daaronder. Anders zou je iemand moeten kiezen om te kunnen lezen wie het
 * is, en dat werkt niet als je precies drie van de tien moet uitzoeken.
 */
import React, { useState } from 'react';
import { ChevronLeft, Check } from 'lucide-react';
import { kleur } from '../../../theme/tokens';
import { basis } from '../stijlen';
import { GroepBadge, PersonaBeeld } from '../onderdelen/GroepEnKlok';

export function KiesGesprekspartners({ api, apiUrl, session, discussion, groepje, personas, onTerug, onGekozen }) {
  const [gekozenIds, setGekozenIds] = useState([]);
  const [openPersona, setOpenPersona] = useState(null);
  // Na het kiezen verdeelt het model de rollen over de personages; dat duurt
  // een paar tellen en zonder bericht lijkt de knop niet te werken.
  const [rollenBezig, setRollenBezig] = useState(false);

  // De leerkracht stelt in hoeveel gesprekspartners een groepje kiest. Zijn
  // er minder personages beschikbaar dan dat, dan zou "kies er precies vier"
  // uit drie een doodloop zijn: de knop wordt nooit actief. Daarom nooit
  // meer vragen dan er te kiezen valt.
  const teKiezen = Math.min(session?.aantal_personas ?? 0, personas.length || (session?.aantal_personas ?? 0));

  const wissel = (personaId) => {
    if (gekozenIds.includes(personaId)) setGekozenIds(gekozenIds.filter(id => id !== personaId));
    else if (gekozenIds.length < teKiezen) setGekozenIds([...gekozenIds, personaId]);
  };

  const start = async () => {
    if (gekozenIds.length !== teKiezen) { alert('Kies precies ' + teKiezen + ' gesprekspartners'); return; }
    setRollenBezig(true);
    try {
      const data = await api.kiesPersonas(discussion.discussion_id, gekozenIds);
      if (data.success) await onGekozen(data.data.personas);
    } catch (err) { console.error('Fout:', err); }
  };

  const open = openPersona && personas.find(p => p.id === openPersona);

  return (
    <div style={styles.card}>
      <div style={styles.stepHeader}>
        <span style={styles.stepNumber}>2</span>
        <div><h2 style={styles.stepTitle}>Kies je gesprekspartners</h2><p style={styles.stepSubtitle}>Kies er {teKiezen} uit deze {personas.length}</p></div>
      </div>
      <GroepBadge groepje={groepje} discussion={discussion} />
      <div style={styles.personaCounter}>{gekozenIds.length} / {teKiezen} gekozen</div>
      <p style={styles.uitleg}>Tik op iemand om te lezen wie het is.</p>
      <div style={styles.personaGrid}>
        {personas.map(persona => {
          const gekozen = gekozenIds.includes(persona.id);
          const staatOpen = openPersona === persona.id;
          return (
            <button key={persona.id} onClick={() => setOpenPersona(staatOpen ? null : persona.id)}
              style={gekozen ? styles.personaTegelGekozen : staatOpen ? styles.personaTegelOpen : styles.personaTegel}>
              <div style={styles.personaAvatar}><PersonaBeeld apiUrl={apiUrl} persona={persona} grootte={72} /></div>
              <div style={styles.personaNaam}>{persona.naam}</div>
              {gekozen && <span style={styles.checkmark}><Check size={18} /></span>}
            </button>
          );
        })}
      </div>

      {open && (() => {
        const gekozen = gekozenIds.includes(open.id);
        const vol = gekozenIds.length >= teKiezen;
        return (
          <div style={styles.persoonBlok}>
            <div style={styles.persoonBeeld}><PersonaBeeld apiUrl={apiUrl} persona={open} grootte={96} /></div>
            <div style={styles.persoonTekst}>
              <div style={styles.persoonNaam}>{open.naam}</div>
              <p style={styles.persoonToelichting}>{open.toelichting}</p>
              <button onClick={() => wissel(open.id)} disabled={!gekozen && vol}
                style={gekozen ? styles.btnSecondary : (vol ? styles.btnDisabled : styles.btn)}>
                {gekozen ? 'Haal weg' : vol ? 'Je hebt er al ' + teKiezen : 'Kies ' + open.naam.split(' ')[0]}
              </button>
            </div>
          </div>
        );
      })()}
      <div style={styles.buttonRow}>
        <button onClick={onTerug} style={styles.btnSecondary}><ChevronLeft size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Terug</button>
        <button onClick={start} disabled={rollenBezig}
          style={gekozenIds.length === teKiezen && !rollenBezig ? styles.btn : styles.btnDisabled}>
          {rollenBezig ? 'De rollen worden verdeeld…' : 'Start de chat →'}
        </button>
      </div>
    </div>
  );
}

const tegel = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '14px 10px', background: 'white', border: `2px solid ${kleur.lijnTegel}`, borderRadius: '14px', cursor: 'pointer', position: 'relative', font: 'inherit', textAlign: 'center' };

const styles = {
  ...basis,
  personaCounter: { textAlign: 'center', fontSize: '16px', color: kleur.inkt, fontWeight: 'bold', marginBottom: '16px' },
  personaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px', marginBottom: '8px' },
  personaTegel: tegel,
  personaTegelOpen: { ...tegel, border: `2px solid ${kleur.accent}` },
  personaTegelGekozen: { ...tegel, background: kleur.accentLicht, border: `2px solid ${kleur.accent}` },
  personaAvatar: { width: '72px', height: '72px', background: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 },
  personaNaam: { fontWeight: 'bold', fontSize: '16px', color: kleur.inkt },
  checkmark: { position: 'absolute', top: '6px', right: '6px', background: kleur.accent, color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  persoonBlok: { display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap', background: kleur.vlakKoel, border: `1px solid ${kleur.lijn}`, borderRadius: '14px', padding: '16px', marginTop: '16px' },
  persoonBeeld: { flexShrink: 0 },
  persoonTekst: { flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' },
  persoonNaam: { fontWeight: 'bold', fontSize: '17px' },
  persoonToelichting: { margin: 0, fontSize: '15px', lineHeight: 1.5, color: kleur.tekstDonker },
};
